/**
 * Central date service for handling timezone conversions and formatting
 * Ensures consistent date handling across the application
 */

import { formatInTimeZone, zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { format, parseISO } from 'date-fns';

/**
 * Formats a date in the specified timezone
 * @param {Date|string} date - The date to format
 * @param {string} formatStr - Format string for date-fns
 * @param {string} timezone - IANA timezone (e.g., 'America/Chicago')
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (date, formatStr, timezone) => {
  if (!date) return '';
  
  try {
    // Ensure we have a Date object
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Use date-fns-tz to format in the correct timezone
    return formatInTimeZone(dateObj, timezone, formatStr);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return 'Invalid date';
  }
};

/**
 * Converts a local date to UTC
 * @param {Date|string} date - Local date to convert
 * @param {string} timezone - Source timezone IANA identifier
 * @returns {Date} Date in UTC
 */
export const convertToUTC = (date, timezone) => {
  if (!date) return null;
  
  try {
    // Ensure we have a Date object
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Convert to UTC using the specified timezone
    return zonedTimeToUtc(dateObj, timezone);
  } catch (error) {
    console.error('Error converting date to UTC:', error);
    return null;
  }
};

/**
 * Converts a UTC date to the user's local timezone
 * @param {Date|string} utcDate - UTC date to convert
 * @param {string} timezone - Target timezone IANA identifier
 * @returns {Date} Date in the user's timezone
 */
export const convertFromUTC = (utcDate, timezone) => {
  if (!utcDate) return null;
  
  try {
    // Ensure we have a Date object
    const dateObj = typeof utcDate === 'string' ? parseISO(utcDate) : utcDate;
    
    // Convert from UTC to the specified timezone
    return utcToZonedTime(dateObj, timezone);
  } catch (error) {
    console.error('Error converting date from UTC:', error);
    return null;
  }
};

/**
 * Combine date and time fields into a single UTC Date object
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:MM format
 * @param {string} timezone - Source timezone IANA identifier
 * @returns {Date} Combined date and time in UTC
 */
export const combineDateTimeToUTC = (dateStr, timeStr, timezone) => {
  if (!dateStr) return null;
  
  try {
    // Create a date string in ISO format
    const localISOString = `${dateStr}T${timeStr || '00:00'}:00`;
    
    // Parse the ISO string
    const localDate = parseISO(localISOString);
    
    // Convert to UTC
    return convertToUTC(localDate, timezone);
  } catch (error) {
    console.error('Error combining date and time to UTC:', error);
    return null;
  }
};

/**
 * Format date for display in the user's timezone
 * @param {Date|string} utcDate - UTC date to format
 * @param {string} formatStr - Format string (default: 'MMM dd, yyyy')
 * @param {string} timezone - Target timezone IANA identifier
 * @returns {string} Formatted date in user's timezone
 */
export const formatDateForDisplay = (utcDate, formatStr = 'MMM dd, yyyy', timezone) => {
  if (!utcDate) return '';
  
  try {
    return formatDateInTimezone(utcDate, formatStr, timezone);
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return 'Invalid date';
  }
};

/**
 * Format time for display in the user's timezone
 * @param {Date|string} utcDate - UTC date to format
 * @param {string} formatStr - Format string (default: 'h:mm a')
 * @param {string} timezone - Target timezone IANA identifier
 * @returns {string} Formatted time in user's timezone
 */
export const formatTimeForDisplay = (utcDate, formatStr = 'h:mm a', timezone) => {
  if (!utcDate) return '';
  
  try {
    return formatDateInTimezone(utcDate, formatStr, timezone);
  } catch (error) {
    console.error('Error formatting time for display:', error);
    return 'Invalid time';
  }
};

/**
 * Format date and time for display in the user's timezone
 * @param {Date|string} utcDate - UTC date to format
 * @param {string} formatStr - Format string (default: 'MMM dd, yyyy h:mm a')
 * @param {string} timezone - Target timezone IANA identifier
 * @returns {string} Formatted date and time in user's timezone
 */
export const formatDateTimeForDisplay = (utcDate, formatStr = 'MMM dd, yyyy h:mm a', timezone) => {
  if (!utcDate) return '';
  
  try {
    return formatDateInTimezone(utcDate, formatStr, timezone);
  } catch (error) {
    console.error('Error formatting date and time for display:', error);
    return 'Invalid date/time';
  }
};

/**
 * Debug function to show a date in multiple formats and timezones
 * @param {Date|string} date - The date to debug
 * @param {string} userTimezone - User's timezone IANA identifier
 * @returns {Object} Various formatted versions of the date
 */
export const debugDate = (date, userTimezone) => {
  if (!date) return { error: 'No date provided' };
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    return {
      original: date,
      iso: dateObj.toISOString(),
      utcString: dateObj.toUTCString(),
      userTimezone: {
        timezone: userTimezone,
        full: formatDateInTimezone(dateObj, 'yyyy-MM-dd HH:mm:ss.SSS zzz', userTimezone),
        date: formatDateInTimezone(dateObj, 'yyyy-MM-dd', userTimezone),
        time: formatDateInTimezone(dateObj, 'HH:mm:ss', userTimezone)
      },
      systemTimezone: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        full: format(dateObj, 'yyyy-MM-dd HH:mm:ss.SSS'),
        date: format(dateObj, 'yyyy-MM-dd'),
        time: format(dateObj, 'HH:mm:ss')
      }
    };
  } catch (error) {
    console.error('Error in debugDate:', error);
    return { error: 'Error processing date', message: error.message };
  }
};